import { useAppContext } from '@cherre-frontend/core';
import { useParams } from 'react-router-dom';
import {
  GraphQLReturn,
  graphQLSelector,
  useCherreValue,
} from '@cherre-frontend/data-fetching';
import jsonLogic from 'json-logic-js';
import { parse } from 'date-fns';
import { toZonedTime, format as formatTZ } from 'date-fns-tz';
import { useCallback, useLayoutEffect } from 'react';
import { graphql } from 'react-relay';
import { z } from 'zod';
import { constSelector } from 'recoil';
import { useJsonLogicGetIngestionConfigBySubmissionQuery } from './__generated__/useJsonLogicGetIngestionConfigBySubmissionQuery.graphql';
import { useJsonLogicGetIngestionConfigByPropertyBatchQuery } from './__generated__/useJsonLogicGetIngestionConfigByPropertyBatchQuery.graphql';

type IngestionConfig =
  | {
      date_formats: { [k: string]: string };
      numeric_formats: { [k: string]: string | number };
    }
  | undefined;

// add custom operations to json logic
jsonLogic.add_operation('fromEntries', (...params) =>
  Object.fromEntries(params)
);

/**
 * Converts the input to a number if it is a valid number or a numeric string.
 *
 * @param a - The input value to parse. Can be of any type, but typically a number or string.
 * @returns The parsed number if the input is a valid number or numeric string; otherwise, undefined.
 */
const parseNumber = (a: any): number | undefined => {
  if (typeof a === 'number' && !isNaN(a)) {
    return a;
  } else if (typeof a === 'string' && !isNaN(Number(a))) {
    return Number(a);
  } else {
    return undefined;
  }
};

const formatNumber = (a: any, providerConfig?: IngestionConfig) => {
  const n = parseNumber(a);
  if (n === undefined) {
    return { integerPart: '', decimalPart: '' };
  }
  const nStr = Math.abs(a).toFixed(
    (providerConfig?.numeric_formats?.decimal_places as number) ?? 2
  );
  const x = nStr.split('.');
  let x1 = x[0];
  const x2 =
    x.length > 1
      ? (providerConfig?.numeric_formats?.decimal_separator ?? '.') + x[1]
      : '';
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(
      rgx,
      '$1' +
        (providerConfig?.numeric_formats?.thousands_separator ?? ',') +
        '$2'
    );
  }

  return { integerPart: x1, decimalPart: x2 };
};

jsonLogic.add_operation('percent', (a, parentheses = false) => {
  const n = (parseNumber(a) ?? 0) * 100;

  if (n < 0 && parentheses) {
    return `(${Math.abs(n).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}%)`;
  }

  const value = n.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  return `${value}%`;
});

jsonLogic.add_operation('percentage', (a, b = 2) => {
  const n = parseNumber(a) ?? 0;
  const value = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: b,
  });
  return `${value}%`;
});

jsonLogic.add_operation('report_numeric', (a, b = 2) => {
  const n = parseNumber(a) ?? 0;
  const value = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: b,
  });
  return value;
});
jsonLogic.add_operation('coalesce', (a, b) => {
  return a ?? b;
});

const $providerConfigBySubmission = (submission_id: number) => {
  return graphQLSelector({
    query: graphql`
      query useJsonLogicGetIngestionConfigBySubmissionQuery(
        $submissionId: Int
      ) {
        sys_submissions(where: { submission_id: { _eq: $submissionId } }) {
          provider {
            provider_id
            provider_name
            ingestion_config
          }
        }
      }
    ` as GraphQLReturn<useJsonLogicGetIngestionConfigBySubmissionQuery>,
    mapVariables: () => () => ({ submissionId: submission_id }),
    mapResponse: (data) =>
      (data.sys_submissions[0]?.provider?.ingestion_config ??
        {}) as unknown as IngestionConfig,
  });
};

const $providerConfigByPropertyBatch = (property_batch_id: number) => {
  return graphQLSelector({
    query: graphql`
      query useJsonLogicGetIngestionConfigByPropertyBatchQuery(
        $properyBatchId: Int
      ) {
        sys_property_batches(
          where: { property_batch_id: { _eq: $properyBatchId } }
        ) {
          submission {
            provider {
              provider_id
              provider_name
              ingestion_config
            }
          }
        }
      }
    ` as GraphQLReturn<useJsonLogicGetIngestionConfigByPropertyBatchQuery>,
    mapVariables: () => () => ({ properyBatchId: property_batch_id }),
    mapResponse: (data) =>
      (data.sys_property_batches[0]?.submission.provider?.ingestion_config ??
        {}) as unknown as IngestionConfig,
  });
};

export const useJsonLogic = () => {
  const { logger, telemetry } = useAppContext();
  const params = useParams<{
    submission_id: string;
    property_batch_id: string;
  }>();
  const submission_id = Number(params.submission_id);
  const property_batch_id = Number(params.property_batch_id);

  const emptyConfigSelector = constSelector(undefined);
  const selectorBySubmission =
    submission_id > 0
      ? $providerConfigBySubmission(submission_id)()
      : undefined;
  const selectorByBatchId =
    property_batch_id > 0
      ? $providerConfigByPropertyBatch(property_batch_id)()
      : undefined;
  const configSelector =
    selectorBySubmission || selectorByBatchId || emptyConfigSelector;
  const providerConfig = useCherreValue(configSelector);

  useLayoutEffect(() => {
    jsonLogic.add_operation('report_currency', (a, useCurrencySymbol) => {
      const nStr = Math.abs(Number(a)).toFixed(
        (providerConfig?.numeric_formats?.decimal_places as number) ?? 2
      );
      const x = nStr.split('.');
      let x1 = x[0];
      let x2 =
        x.length > 1
          ? (providerConfig?.numeric_formats?.decimal_separator ?? '.') + x[1]
          : '';
      const rgx = /(\d+)(\d{3})/;
      while (rgx.test(x1)) {
        x1 = x1.replace(
          rgx,
          '$1' +
            (providerConfig?.numeric_formats?.thousands_separator ?? ',') +
            '$2'
        );
      }
      let prefix = '';
      if (Number(a) < 0) {
        prefix += '-';
      }
      if (useCurrencySymbol ?? true) {
        if (providerConfig?.numeric_formats?.currency_position === 'after') {
          x2 += providerConfig?.numeric_formats?.currency ?? '$';
        } else {
          prefix += providerConfig?.numeric_formats?.currency ?? '$';
        }
      }
      return prefix + x1 + x2;
    });
    jsonLogic.add_operation(
      'currency',
      (a, showCurrency = true, showNegative = true) => {
        let { integerPart: x1, decimalPart: x2 } = formatNumber(
          a,
          providerConfig
        );

        let currency = '';
        let beforeCurrency = '';
        let afterCurrency = '';

        if (showCurrency) {
          currency =
            (providerConfig?.numeric_formats?.currency as string) ?? '$';
        }

        if (providerConfig?.numeric_formats?.currency_position === 'after') {
          afterCurrency = currency;
        } else {
          beforeCurrency = currency;
        }

        if (showNegative && Number(a) < 0) {
          if (
            providerConfig?.numeric_formats?.negative_sign === 'parentheses'
          ) {
            x1 = `(${beforeCurrency}${x1}`;
            x2 = `${x2}${afterCurrency})`;
          } else {
            x1 = `${beforeCurrency}-${x1}${afterCurrency}`;
          }
        } else if (showCurrency) {
          x1 = `${beforeCurrency}${x1}`;
          x2 = `${x2}${afterCurrency}`;
        }

        return x1 + x2;
      }
    );
    jsonLogic.add_operation('numeric', (a, showNegative = true) => {
      let { integerPart: x1, decimalPart: x2 } = formatNumber(
        a,
        providerConfig
      );

      if (showNegative && (parseNumber(a) ?? 0) < 0) {
        if (providerConfig?.numeric_formats?.negative_sign === 'parentheses') {
          x1 = `(${x1}`;
          x2 = `${x2})`;
        } else {
          x1 = `-${x1}`;
        }
      }
      return x1 + x2;
    });
    jsonLogic.add_operation('integer', (a, showNegative = true) => {
      let { integerPart: x1 } = formatNumber(a, providerConfig);

      if (showNegative && (parseNumber(a) ?? 0) < 0) {
        if (providerConfig?.numeric_formats?.negative_sign === 'parentheses') {
          x1 = `(${x1})`;
        } else {
          x1 = `-${x1}`;
        }
      }
      return x1;
    });
    const formatDate = (a, type, defaultFormat?) => {
      // default to defaultFormat if providerConfig is not defined
      let dateFormat = defaultFormat;
      let standardFormat = '';
      let parsedDate;

      switch (type) {
        case 'date':
          standardFormat = 'dd/MM/yyyy';
          parsedDate = parse(a, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date());
          if (providerConfig?.date_formats?.date) {
            dateFormat =
              providerConfig?.date_formats?.date?.replace(
                /\//g,
                providerConfig?.date_formats?.separator ?? '/'
              ) ?? standardFormat;
          }
          break;
        case 'yearmonth':
          standardFormat = 'MM/yyyy';
          parsedDate = parse(a, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date());
          if (providerConfig?.date_formats?.yearmonth) {
            dateFormat =
              providerConfig?.date_formats?.yearmonth?.replace(
                /\//g,
                providerConfig?.date_formats?.separator ?? '/'
              ) ?? standardFormat;
          }
          break;
        case 'datetime':
          standardFormat = 'HH:mm:ss dd/MM/yyyy';
          parsedDate = parse(a, "yyyy-MM-dd'T'HH:mm:ss.SSSX", new Date());
          if (providerConfig?.date_formats?.datetime) {
            dateFormat =
              providerConfig?.date_formats?.datetime?.replace(
                /\//g,
                providerConfig?.date_formats?.separator ?? '/'
              ) ?? standardFormat;
          }
          break;
      }

      const utcDate = toZonedTime(parsedDate, 'UTC');
      const d = formatTZ(utcDate, dateFormat, { timeZone: 'UTC' });

      if (d === 'Invalid date') {
        return '';
      }
      return d;
    };
    jsonLogic.add_operation('date', (a, b) => {
      return formatDate(a, 'date', b);
    });
    jsonLogic.add_operation('yearmonth', (a, b) => {
      return formatDate(a, 'yearmonth', b);
    });
    jsonLogic.add_operation('datetime', (a, b) => {
      return formatDate(a, 'datetime', b);
    });
  }, [providerConfig]);
  return useCallback(<T>(logic: any, data: any, schema?: z.ZodType<T>) => {
    try {
      const result = jsonLogic.apply(logic, data);
      return schema ? schema.parse(result) : result ?? null;
    } catch (e) {
      logger.error(e);
      telemetry.captureException(e);
      return null;
    }
  }, []);
};
